import React from "react";

type TimeRange = "1d" | "7d" | "1m" | "6m" | "1y";

interface BreakdownValue {
    label: string;
    value: string | number;
    color: string;
    unit?: string;
}

interface HealthBreakdownProps<T = any> {
    selectedRange: TimeRange;
    filteredData: T[];
    breakdownTitle: string;
    loading: boolean;
    error: string | null;
    dataTypeLabel: string;
    getKey: (item: T, selectedRange: TimeRange) => string;
    getTimeDisplay: (item: T, selectedRange: TimeRange) => string;
    getValues: (item: T) => BreakdownValue[];
    noDataMessage: string;
}

export const HealthBreakdown = <T,>({
    selectedRange,
    filteredData,
    breakdownTitle,
    loading,
    error,
    dataTypeLabel,
    getKey,
    getTimeDisplay,
    getValues,
    noDataMessage,
}: HealthBreakdownProps<T>) => {
    return (
        <div className="px-4">
            <h3 className="mb-4 text-lg font-semibold">{breakdownTitle}</h3>
            <div className="space-y-3">
                {filteredData.length > 0 && !loading && !error ? (
                    filteredData
                        .slice()
                        .reverse()
                        .map((item, _index) => {
                            const values = getValues(item);
                            return (
                                <div
                                    key={getKey(item, selectedRange)}
                                    className="rounded-xl bg-gray-900/50 p-4"
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="font-medium">
                                                {getTimeDisplay(item, selectedRange)}
                                            </div>
                                            <div className="mt-1 text-sm text-gray-400">
                                                {dataTypeLabel}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="flex items-center space-x-4">
                                                {values.map((value, index) => (
                                                    <div key={index} className="text-center">
                                                        <div className="flex items-center space-x-1">
                                                            <div
                                                                className="h-2 w-2 rounded-full"
                                                                style={{ backgroundColor: value.color }}
                                                            ></div>
                                                            <span className="text-sm text-gray-400">
                                                                {value.label}
                                                            </span>
                                                        </div>
                                                        <div className="font-medium">
                                                            {value.value} {value.unit || ""}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                ) : (
                    <div className="py-8 text-center">
                        <div className="mb-2 text-sm text-gray-500">
                            <svg
                                width="32"
                                height="32"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="mx-auto mb-3"
                            >
                                <path
                                    d="M9 11H15M9 15H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17ZM17 21V11H13V7H7V19H17Z"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                        <div className="text-sm text-gray-400">{noDataMessage}</div>
                        <div className="mt-1 text-xs text-gray-500">
                            for the selected time range
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
